/**
 * i18n Configuration
 *
 * Estrutura de namespaces:
 *  - "common"  → strings compartilhadas por todas as views (botões, campos genéricos, erros comuns)
 *  - "<view>"  → strings exclusivas de cada view/módulo (ex.: "grupos", "produtos", "clientes")
 *
 * Idiomas suportados:
 *  - pt-BR  (padrão / fallback)
 *  - pt-PT
 *
 * Como usar em um componente:
 *
 *   import { useTranslation } from 'react-i18next';
 *
 *   const { t } = useTranslation('common');
 *   const { t: tGrupos } = useTranslation('grupos');
 *
 *   // Uso:
 *   t('buttons.cancel')           // → "Cancelar"
 *   tGrupos('modal.new.title')    // → "Novo Grupo"
 *   tGrupos('delete.confirm', { descricao: 'Bebidas' })  // interpolação
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// IMPORTANTE: Ajuste o caminho de importação conforme a estrutura do seu projeto
import { obterStorage } from "../utils/secureStorage";
import ptBRCadastroEmail from "./locales/pt-BR/cadastroEmail.json";
import ptBRCadastroProdutos from "./locales/pt-BR/cadastroProdutos.json";
import ptBRCanaisVenda from "./locales/pt-BR/canaisVenda.json";
import ptBRCategoriasSangria from "./locales/pt-BR/categoriasSangria.json";
import ptBRConfirmacaoImportacaoNfe from "./locales/pt-BR/confirmacaoImportacaoNfe.json";
// ─── pt-BR ────────────────────────────────────────────────────────────────────
import ptBRCommon from "./locales/pt-BR/common.json";
import ptBRModalSelecao from "./locales/pt-BR/components/modalSelecao.json";
import ptBRModalSelecionarCaixas from "./locales/pt-BR/components/modalSelecionarCaixas.json";
import ptBRModalSelecionarClientes from "./locales/pt-BR/components/modalSelecionarClientes.json";
import ptBRModalSelecionarInsumos from "./locales/pt-BR/components/modalSelecionarInsumos.json";
import ptBRModalSelecionarLojas from "./locales/pt-BR/components/modalSelecionarLojas.json";
import ptBRModalSelecionarTurnos from "./locales/pt-BR/components/modalSelecionarTurnos.json";
import ptBRSelecionarFornecedores from "./locales/pt-BR/components/selecionarFornecedores.json";
import ptBRSelecionarFornecedoresPaginado from "./locales/pt-BR/components/selecionarFornecedoresPaginado.json";
import ptBRSelecionarGrupos from "./locales/pt-BR/components/selecionarGrupos.json";
import ptBRConsultasRapidasCaixa from "./locales/pt-BR/consultasRapidas/caixa.json";
import ptBRConsultasRapidasAtendimentos from "./locales/pt-BR/consultasRapidasAtendimentos.json";
import ptBRContasCorrentes from "./locales/pt-BR/contasCorrentes.json";
import ptBRDownloadAplicativos from "./locales/pt-BR/downloadAplicativos.json";
import ptBREmissoresFiscais from "./locales/pt-BR/emissoresFiscais.json";
import ptBRFormasDePagamento from "./locales/pt-BR/formasDePagamento.json";
import ptBRFuncionamentoEmpresa from "./locales/pt-BR/funcionamentoEmpresa.json";
import ptBRGrupos from "./locales/pt-BR/grupos.json";
import ptBRLayoutsDeConferencia from "./locales/pt-BR/layoutsDeConferencia.json";
import ptBRMeusDispositivos from "./locales/pt-BR/meusDispositivos.json";
import ptBRMinhaEmpresa from "./locales/pt-BR/minhaEmpresa.json";
import ptBRMinhasBalancas from "./locales/pt-BR/minhasBalancas.json";
import ptBRMinhasImpressoras from "./locales/pt-BR/minhasImpressoras.json";
import ptBRPedidosDeCompras from "./locales/pt-BR/operacional/financas/compras/pedidosDeCompras.json";
import ptBRRecebimentoMercadorias from "./locales/pt-BR/operacional/financas/compras/recebimentoMercadorias.json";
import ptBROperacionalFinancasConfiguracoes from "./locales/pt-BR/operacional/financas/configuracoes.json";
import ptBRFornecedoresCredores from "./locales/pt-BR/operacional/financas/fornecedoresCredores/fornecedoresCredores.json";
import ptBRContasReceber from "./locales/pt-BR/operacional/financas/movimentacoes/contasReceber.json";
import ptBRCadastroBandeiras from "./locales/pt-BR/operacional/financas/parametros/cadastroBandeiras.json";
import ptBRCategoriaSangria from "./locales/pt-BR/operacional/financas/parametros/categoriaSangria.json";
import ptBRCentroDeCustos from "./locales/pt-BR/operacional/financas/parametros/centroDeCustos.json";
import ptBRCFOP from "./locales/pt-BR/operacional/financas/parametros/CFOP.json";
import ptBRContasCorrente from "./locales/pt-BR/operacional/financas/parametros/contasCorrente.json";
import ptBRPlanoDeContas from "./locales/pt-BR/operacional/financas/parametros/planoDeContas.json";
import ptBRRepresentantes from "./locales/pt-BR/operacional/financas/representantes/representantes.json";
import ptBRPedidosDeVendas from "./locales/pt-BR/operacional/financas/vendas/pedidosDeVendas.json";
import ptBRPagamento from "./locales/pt-BR/pagamento.json";
import ptBRParametrosModulosPub from "./locales/pt-BR/parametrosModulosPub.json";
import ptBRPerfisUsuarios from "./locales/pt-BR/perfisUsuarios.json";
import ptBRCadastraisClientes from "./locales/pt-BR/relatorios/cadastrais/clientes.json";
import ptBRCadastraisFichaTecnica from "./locales/pt-BR/relatorios/cadastrais/fichaTecnica.json";
import ptBRCadastraisProdutos from "./locales/pt-BR/relatorios/cadastrais/produtos.json";
import ptBRCadastraisTributacaoICMS from "./locales/pt-BR/relatorios/cadastrais/tributacaoICMS.json";
import ptBRCadastraisTributacaoPiscofins from "./locales/pt-BR/relatorios/cadastrais/tributacaoPiscofins.json";
import ptBRCashback from "./locales/pt-BR/relatorios/cashback.json";
import ptBRRelatoriosEstoqueDisponivel from "./locales/pt-BR/relatorios/estoque/disponivel.json";
import ptBRRelatoriosEstoqueEmFalta from "./locales/pt-BR/relatorios/estoque/emFalta.json";
import ptBRRelatoriosEstoqueGeral from "./locales/pt-BR/relatorios/estoque/geral.json";
import ptBRRelatoriosEstoqueHistoricoMovimentacoes from "./locales/pt-BR/relatorios/estoque/historicoMovimentacoes.json";
import ptBREstoqueMovimentacoesInsumos from "./locales/pt-BR/relatorios/estoque/movimentacoesInsumos.json";
import ptBRCancelamentosItensCancelados from "./locales/pt-BR/relatorios/faturamento/cancelamentos/itensCancelados.json";
import ptBRCurvaABC from "./locales/pt-BR/relatorios/faturamento/curvaABC.json";
import ptBRControleTempoProducao from "./locales/pt-BR/relatorios/faturamento/modulos/controleTempoProducao.json";
import ptBRDelivery from "./locales/pt-BR/relatorios/faturamento/modulos/delivery.json";
import ptBRAnualMesMes from "./locales/pt-BR/relatorios/faturamento/monetario/anualMesMes.json";
import ptBRMonetarioAtendimentos from "./locales/pt-BR/relatorios/faturamento/monetario/atendimentos.json";
import ptBRComissaoVendedor from "./locales/pt-BR/relatorios/faturamento/monetario/comissaoVendedor.json";
import ptBRRelatorioFormasDePagamento from "./locales/pt-BR/relatorios/faturamento/monetario/formasDePagamento.json";
import ptBRPorModulo from "./locales/pt-BR/relatorios/faturamento/monetario/porModulo.json";
import ptBRPorPeriodo from "./locales/pt-BR/relatorios/faturamento/monetario/porPeriodo.json";
import ptBRTaxasServico from "./locales/pt-BR/relatorios/faturamento/monetario/taxasServico.json";
import ptBRProdutosVendidosItensVendidos from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/itensVendidos.json";
import ptBRProdutosVendidosPorGrupo from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/porGrupo.json";
import ptBRProdutosVendidosPorPacote from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/porPacote.json";
import ptBRProdutosVendidosPorTabelaPreco from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/porTabelaPreco.json";
import ptBRProdutosVendidosPorVendedor from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/porVendedor.json";
import ptBRProdutosVendidosTop10 from "./locales/pt-BR/relatorios/faturamento/produtosVendidos/top10.json";
import ptBRRelatoriosFinancasContasAPagar from "./locales/pt-BR/relatorios/financas/contasAPagar.json";
import ptBRRelatoriosFinancasContasAReceber from "./locales/pt-BR/relatorios/financas/contasAReceber.json";
import ptBRFicaisListagemAuxEscrituracaoFiscal from "./locales/pt-BR/relatorios/fiscais/listagemAuxEscrituracaoFiscal.json";
import ptBRPedidosPedidosDeVenda from "./locales/pt-BR/relatorios/pedidos/pedidosDeVenda.json";
import ptBRServicosDoSistema from "./locales/pt-BR/servicosDoSistema.json";
import ptBRTransacoes from "./locales/pt-BR/transacoes.json";
import ptBRTributacaoUfDestino from "./locales/pt-BR/tributacaoUfDestino.json";
import ptBRUsuarios from "./locales/pt-BR/usuarios.json";
import ptPTCadastroEmail from "./locales/pt-PT/cadastroEmail.json";
import ptPTCadastroProdutos from "./locales/pt-PT/cadastroProdutos.json";
import ptPTCanaisVenda from "./locales/pt-PT/canaisVenda.json";
import ptPTCategoriasSangria from "./locales/pt-PT/categoriasSangria.json";
import ptBRChatbotRelatorioSatisfacao from "./locales/pt-BR/plataforma/chatbot/relatorioSatisfacao.json";
import ptBRChatbotConfiguracao from "./locales/pt-BR/plataforma/chatbot/chatbotConfiguracao.json";
import ptBRChatbotSuporte from "./locales/pt-BR/components/chatbotSuporte.json";
import ptPTConfirmacaoImportacaoNfe from "./locales/pt-PT/confirmacaoImportacaoNfe.json";
import ptBRAssistenteIA from "./locales/pt-BR/components/assistenteIA.json";
// ─── pt-PT ────────────────────────────────────────────────────────────────────
import ptPTCommon from "./locales/pt-PT/common.json";
import ptPTModalSelecao from "./locales/pt-PT/components/modalSelecao.json";
import ptPTModalSelecionarCaixas from "./locales/pt-PT/components/modalSelecionarCaixas.json";
import ptPTModalSelecionarClientes from "./locales/pt-PT/components/modalSelecionarClientes.json";
import ptPTModalSelecionarInsumos from "./locales/pt-PT/components/modalSelecionarInsumos.json";
import ptPTModalSelecionarLojas from "./locales/pt-PT/components/modalSelecionarLojas.json";
import ptPTModalSelecionarTurnos from "./locales/pt-PT/components/modalSelecionarTurnos.json";
import ptPTSelecionarFornecedores from "./locales/pt-PT/components/selecionarFornecedores.json";
import ptPTSelecionarFornecedoresPaginado from "./locales/pt-PT/components/selecionarFornecedoresPaginado.json";
import ptPTSelecionarGrupos from "./locales/pt-PT/components/selecionarGrupos.json";
import ptPTConsultasRapidasCaixa from "./locales/pt-PT/consultasRapidas/caixa.json";
import ptPTConsultasRapidasAtendimentos from "./locales/pt-PT/consultasRapidasAtendimentos.json";
import ptPTContasCorrentes from "./locales/pt-PT/contasCorrentes.json";
import ptPTDownloadAplicativos from "./locales/pt-PT/downloadAplicativos.json";
import ptPTEmissoresFiscais from "./locales/pt-PT/emissoresFiscais.json";
import ptPTFormasDePagamento from "./locales/pt-PT/formasDePagamento.json";
import ptPTFuncionamentoEmpresa from "./locales/pt-PT/funcionamentoEmpresa.json";
import ptPTGrupos from "./locales/pt-PT/grupos.json";
import ptPTLayoutsDeConferencia from "./locales/pt-PT/layoutsDeConferencia.json";
import ptPTMeusDispositivos from "./locales/pt-PT/meusDispositivos.json";
import ptPTMinhaEmpresa from "./locales/pt-PT/minhaEmpresa.json";
import ptPTMinhasBalancas from "./locales/pt-PT/minhasBalancas.json";
import ptPTMinhasImpressoras from "./locales/pt-PT/minhasImpressoras.json";
import ptPTPedidosDeCompras from "./locales/pt-PT/operacional/financas/compras/pedidosDeCompras.json";
import ptPTRecebimentoMercadorias from "./locales/pt-PT/operacional/financas/compras/recebimentoMercadorias.json";
import ptPTOperacionalFinancasConfiguracoes from "./locales/pt-PT/operacional/financas/configuracoes.json";
import ptPTFornecedoresCredores from "./locales/pt-PT/operacional/financas/fornecedoresCredores/fornecedoresCredores.json";
import ptPTContasReceber from "./locales/pt-PT/operacional/financas/movimentacoes/contasReceber.json";
import ptPTCadastroBandeiras from "./locales/pt-PT/operacional/financas/parametros/cadastroBandeiras.json";
import ptPTCategoriaSangria from "./locales/pt-PT/operacional/financas/parametros/categoriaSangria.json";
import ptPTCentroDeCustos from "./locales/pt-PT/operacional/financas/parametros/centroDeCustos.json";
import ptPTCFOP from "./locales/pt-PT/operacional/financas/parametros/CFOP.json";
import ptPTContasCorrente from "./locales/pt-PT/operacional/financas/parametros/contasCorrente.json";
import ptPTPlanoDeContas from "./locales/pt-PT/operacional/financas/parametros/planoDeContas.json";
import ptPTRepresentantes from "./locales/pt-PT/operacional/financas/representantes/representantes.json";
import ptPTPedidosDeVendas from "./locales/pt-PT/operacional/financas/vendas/pedidosDeVendas.json";
import ptPTPagamento from "./locales/pt-PT/pagamento.json";
import ptPTParametrosModulosPub from "./locales/pt-PT/parametrosModulosPub.json";
import ptPTPerfisUsuarios from "./locales/pt-PT/perfisUsuarios.json";
import ptPTCadastraisClientes from "./locales/pt-PT/relatorios/cadastrais/clientes.json";
import ptPTCadastraisFichaTecnica from "./locales/pt-PT/relatorios/cadastrais/fichaTecnica.json";
import ptPTCadastraisProdutos from "./locales/pt-PT/relatorios/cadastrais/produtos.json";
import ptPTCadastraisTributacaoICMS from "./locales/pt-PT/relatorios/cadastrais/tributacaoICMS.json";
import ptPTCadastraisTributacaoPiscofins from "./locales/pt-PT/relatorios/cadastrais/tributacaoPiscofins.json";
import ptPTCashback from "./locales/pt-PT/relatorios/cashback.json";
import ptPTRelatoriosEstoqueDisponivel from "./locales/pt-PT/relatorios/estoque/disponivel.json";
import ptPTRelatoriosEstoqueEmFalta from "./locales/pt-PT/relatorios/estoque/emFalta.json";
import ptPTRelatoriosEstoqueGeral from "./locales/pt-PT/relatorios/estoque/geral.json";
import ptPTRelatoriosEstoqueHistoricoMovimentacoes from "./locales/pt-PT/relatorios/estoque/historicoMovimentacoes.json";
import ptPTEstoqueMovimentacoesInsumos from "./locales/pt-PT/relatorios/estoque/movimentacoesInsumos.json";
import ptPTCancelamentosItensCancelados from "./locales/pt-PT/relatorios/faturamento/cancelamentos/itensCancelados.json";
import ptPTCurvaABC from "./locales/pt-PT/relatorios/faturamento/curvaABC.json";
import ptPTControleTempoProducao from "./locales/pt-PT/relatorios/faturamento/modulos/controleTempoProducao.json";
import ptPTDelivery from "./locales/pt-PT/relatorios/faturamento/modulos/delivery.json";
import ptPTAnualMesMes from "./locales/pt-PT/relatorios/faturamento/monetario/anualMesMes.json";
import ptPTMonetarioAtendimentos from "./locales/pt-PT/relatorios/faturamento/monetario/atendimentos.json";
import ptPTComissaoVendedor from "./locales/pt-PT/relatorios/faturamento/monetario/comissaoVendedor.json";
import ptPTRelatorioFormasDePagamento from "./locales/pt-PT/relatorios/faturamento/monetario/formasDePagamento.json";
import ptPTPorModulo from "./locales/pt-PT/relatorios/faturamento/monetario/porModulo.json";
import ptPTPorPeriodo from "./locales/pt-PT/relatorios/faturamento/monetario/porPeriodo.json";
import ptPTTaxasServico from "./locales/pt-PT/relatorios/faturamento/monetario/taxasServico.json";
import ptPTProdutosVendidosItensVendidos from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/itensVendidos.json";
import ptPTProdutosVendidosPorGrupo from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/porGrupo.json";
import ptPTProdutosVendidosPorPacote from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/porPacote.json";
import ptPTProdutosVendidosPorTabelaPreco from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/porTabelaPreco.json";
import ptPTProdutosVendidosPorVendedor from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/porVendedor.json";
import ptPTProdutosVendidosTop10 from "./locales/pt-PT/relatorios/faturamento/produtosVendidos/top10.json";
import ptPTRelatoriosFinancasContasAPagar from "./locales/pt-PT/relatorios/financas/contasAPagar.json";
import ptPTRelatoriosFinancasContasAReceber from "./locales/pt-PT/relatorios/financas/contasAReceber.json";
import ptPTFicaisListagemAuxEscrituracaoFiscal from "./locales/pt-PT/relatorios/fiscais/listagemAuxEscrituracaoFiscal.json";
import ptPTPedidosPedidosDeVenda from "./locales/pt-PT/relatorios/pedidos/pedidosDeVenda.json";
import ptPTServicosDoSistema from "./locales/pt-PT/servicosDoSistema.json";
import ptPTTransacoes from "./locales/pt-PT/transacoes.json";
import ptPTTributacaoUfDestino from "./locales/pt-PT/tributacaoUfDestino.json";
import ptPTUsuarios from "./locales/pt-PT/usuarios.json";
import ptPTChatbotRelatorioSatisfacao from "./locales/pt-PT/plataforma/chatbot/relatorioSatisfacao.json";
import ptPTChatbotConfiguracao from "./locales/pt-PT/plataforma/chatbot/chatbotConfiguracao.json";
import ptPTChatbotSuporte from "./locales/pt-PT/components/chatbotSuporte.json";
import ptPTAssistenteIA from "./locales/pt-PT/components/assistenteIA.json";
// ─── Recursos agrupados por idioma e namespace ────────────────────────────────
const resources = {
  "pt-BR": {
    common: ptBRCommon,
    consultasRapidasAtendimentos: ptBRConsultasRapidasAtendimentos,
    grupos: ptBRGrupos,
    minhaEmpresa: ptBRMinhaEmpresa,
    dadosEmpresa: ptBRMinhaEmpresa,
    canaisVenda: ptBRCanaisVenda,
    funcionamentoEmpresa: ptBRFuncionamentoEmpresa,
    pagamento: ptBRPagamento,
    transacoes: ptBRTransacoes,
    cadastroEmail: ptBRCadastroEmail,
    cadastroProdutos: ptBRCadastroProdutos,
    minhasBalancas: ptBRMinhasBalancas,
    emissoresFiscais: ptBREmissoresFiscais,
    downloadAplicativos: ptBRDownloadAplicativos,
    minhasImpressoras: ptBRMinhasImpressoras,
    meusDispositivos: ptBRMeusDispositivos,
    formasDePagamento: ptBRFormasDePagamento,
    financasOperacionalConfiguracoes: ptBROperacionalFinancasConfiguracoes,
    layoutsDeConferencia: ptBRLayoutsDeConferencia,
    servicosDoSistema: ptBRServicosDoSistema,
    perfisUsuarios: ptBRPerfisUsuarios,
    usuarios: ptBRUsuarios,
    categoriasSangria: ptBRCategoriasSangria,
    confirmacaoImportacaoNfe: ptBRConfirmacaoImportacaoNfe,
    contasCorrentes: ptBRContasCorrentes,
    cadastraisClientes: ptBRCadastraisClientes,
    cadastraisFichaTecnica: ptBRCadastraisFichaTecnica,
    cadastraisProdutos: ptBRCadastraisProdutos,
    cadastraisTributacaoICMS: ptBRCadastraisTributacaoICMS,
    cadastraisTributacaoPiscofins: ptBRCadastraisTributacaoPiscofins,
    cashback: ptBRCashback,
    modalSelecionarClientes: ptBRModalSelecionarClientes,
    modalSelecionarLojas: ptBRModalSelecionarLojas,
    modalSelecionarInsumos: ptBRModalSelecionarInsumos,
    selecionarGrupos: ptBRSelecionarGrupos,
    selecionarFornecedoresPaginado: ptBRSelecionarFornecedoresPaginado,
    selecionarFornecedores: ptBRSelecionarFornecedores,
    fiscaisListagemAuxEscrituracaoFiscal: ptBRFicaisListagemAuxEscrituracaoFiscal,
    estoqueMovimentacoesInsumos: ptBREstoqueMovimentacoesInsumos,
    relatoriosEstoqueGeral: ptBRRelatoriosEstoqueGeral,
    relatoriosEstoqueHistoricoMovimentacoes: ptBRRelatoriosEstoqueHistoricoMovimentacoes,
    relatoriosEstoqueDisponivel: ptBRRelatoriosEstoqueDisponivel,
    relatoriosEstoqueEmFalta: ptBRRelatoriosEstoqueEmFalta,
    relatoriosFinancasContasAPagar: ptBRRelatoriosFinancasContasAPagar,
    relatoriosFinancasContasAReceber: ptBRRelatoriosFinancasContasAReceber,
    pedidosPedidosDeVenda: ptBRPedidosPedidosDeVenda,
    faturamentoCurvaABC: ptBRCurvaABC,
    faturamentoCancelamentosItensCancelados: ptBRCancelamentosItensCancelados,
    faturamentoProdutosVendidosTop10: ptBRProdutosVendidosTop10,
    faturamentoProdutosVendidosPorTabelaPreco: ptBRProdutosVendidosPorTabelaPreco,
    faturamentoProdutosVendidosPorPacote: ptBRProdutosVendidosPorPacote,
    faturamentoProdutosVendidosPorGrupo: ptBRProdutosVendidosPorGrupo,
    faturamentoProdutosVendidosItensVendidos: ptBRProdutosVendidosItensVendidos,
    faturamentoProdutosVendidosPorVendedor: ptBRProdutosVendidosPorVendedor,
    faturamentoModulosDelivery: ptBRDelivery,
    faturamentoModulosControleTempoProducao: ptBRControleTempoProducao,
    modalSelecionarCaixas: ptBRModalSelecionarCaixas,
    faturamentoMonetarioAnualMesMes: ptBRAnualMesMes,
    faturamentoMonetarioAtendimentos: ptBRMonetarioAtendimentos,
    faturamentoMonetarioTaxasServico: ptBRTaxasServico,
    faturamentoMonetarioComissaoVendedor: ptBRComissaoVendedor,
    faturamentoMonetarioPorPeriodo: ptBRPorPeriodo,
    faturamentoMonetarioPorModulo: ptBRPorModulo,
    faturamentoMonetarioFormasDePagamento: ptBRRelatorioFormasDePagamento,
    operacionalFornecedoresCredores: ptBRFornecedoresCredores,
    financasRepresentantes: ptBRRepresentantes,
    financasParametrosCategoriaSangria: ptBRCategoriaSangria,
    financasParametrosCentroDeCustos: ptBRCentroDeCustos,
    financasParametrosCadastroBandeiras: ptBRCadastroBandeiras,
    financasParametrosPlanoDeContas: ptBRPlanoDeContas,
    financasParametrosContasCorrente: ptBRContasCorrente,
    financasParametrosCFOP: ptBRCFOP,
    financasMovimentacoesContasReceber: ptBRContasReceber,
    financasComprasPedidosDeCompras: ptBRPedidosDeCompras,
    financasComprasRecebimentoMercadorias: ptBRRecebimentoMercadorias,
    financasVendasPedidosDeVendas: ptBRPedidosDeVendas,
    consultasRapidasCaixa: ptBRConsultasRapidasCaixa,
    tributacaoUfDestino: ptBRTributacaoUfDestino,
    modalSelecionarTurnos: ptBRModalSelecionarTurnos,
    modalSelecao: ptBRModalSelecao,
    parametrosModulosPub: ptBRParametrosModulosPub,
    relatorioSatisfacao: ptBRChatbotRelatorioSatisfacao,
    chatbotConfiguracao: ptBRChatbotConfiguracao,
    chatbotSuporte: ptBRChatbotSuporte,
    assistenteIA: ptBRAssistenteIA,
  },
  "pt-PT": {
    common: ptPTCommon,
    consultasRapidasAtendimentos: ptPTConsultasRapidasAtendimentos,
    grupos: ptPTGrupos,
    minhaEmpresa: ptPTMinhaEmpresa,
    dadosEmpresa: ptPTMinhaEmpresa,
    canaisVenda: ptPTCanaisVenda,
    funcionamentoEmpresa: ptPTFuncionamentoEmpresa,
    pagamento: ptPTPagamento,
    transacoes: ptPTTransacoes,
    cadastroEmail: ptPTCadastroEmail,
    cadastroProdutos: ptPTCadastroProdutos,
    minhasBalancas: ptPTMinhasBalancas,
    emissoresFiscais: ptPTEmissoresFiscais,
    downloadAplicativos: ptPTDownloadAplicativos,
    minhasImpressoras: ptPTMinhasImpressoras,
    meusDispositivos: ptPTMeusDispositivos,
    formasDePagamento: ptPTFormasDePagamento,
    financasOperacionalConfiguracoes: ptPTOperacionalFinancasConfiguracoes,
    layoutsDeConferencia: ptPTLayoutsDeConferencia,
    servicosDoSistema: ptPTServicosDoSistema,
    perfisUsuarios: ptPTPerfisUsuarios,
    usuarios: ptPTUsuarios,
    categoriasSangria: ptPTCategoriasSangria,
    confirmacaoImportacaoNfe: ptPTConfirmacaoImportacaoNfe,
    contasCorrentes: ptPTContasCorrentes,
    cadastraisClientes: ptPTCadastraisClientes,
    cadastraisFichaTecnica: ptPTCadastraisFichaTecnica,
    cadastraisProdutos: ptPTCadastraisProdutos,
    cadastraisTributacaoICMS: ptPTCadastraisTributacaoICMS,
    cadastraisTributacaoPiscofins: ptPTCadastraisTributacaoPiscofins,
    cashback: ptPTCashback,
    modalSelecionarClientes: ptPTModalSelecionarClientes,
    modalSelecionarLojas: ptPTModalSelecionarLojas,
    modalSelecionarInsumos: ptPTModalSelecionarInsumos,
    selecionarGrupos: ptPTSelecionarGrupos,
    selecionarFornecedoresPaginado: ptPTSelecionarFornecedoresPaginado,
    selecionarFornecedores: ptPTSelecionarFornecedores,
    fiscaisListagemAuxEscrituracaoFiscal: ptPTFicaisListagemAuxEscrituracaoFiscal,
    estoqueMovimentacoesInsumos: ptPTEstoqueMovimentacoesInsumos,
    relatoriosEstoqueGeral: ptPTRelatoriosEstoqueGeral,
    relatoriosEstoqueHistoricoMovimentacoes: ptPTRelatoriosEstoqueHistoricoMovimentacoes,
    relatoriosEstoqueDisponivel: ptPTRelatoriosEstoqueDisponivel,
    relatoriosEstoqueEmFalta: ptPTRelatoriosEstoqueEmFalta,
    relatoriosFinancasContasAPagar: ptPTRelatoriosFinancasContasAPagar,
    relatoriosFinancasContasAReceber: ptPTRelatoriosFinancasContasAReceber,
    pedidosPedidosDeVenda: ptPTPedidosPedidosDeVenda,
    faturamentoCurvaABC: ptPTCurvaABC,
    faturamentoCancelamentosItensCancelados: ptPTCancelamentosItensCancelados,
    faturamentoProdutosVendidosTop10: ptPTProdutosVendidosTop10,
    faturamentoProdutosVendidosPorTabelaPreco: ptPTProdutosVendidosPorTabelaPreco,
    faturamentoProdutosVendidosPorPacote: ptPTProdutosVendidosPorPacote,
    faturamentoProdutosVendidosPorGrupo: ptPTProdutosVendidosPorGrupo,
    faturamentoProdutosVendidosItensVendidos: ptPTProdutosVendidosItensVendidos,
    faturamentoProdutosVendidosPorVendedor: ptPTProdutosVendidosPorVendedor,
    faturamentoModulosDelivery: ptPTDelivery,
    faturamentoModulosControleTempoProducao: ptPTControleTempoProducao,
    modalSelecionarCaixas: ptPTModalSelecionarCaixas,
    faturamentoMonetarioAnualMesMes: ptPTAnualMesMes,
    faturamentoMonetarioAtendimentos: ptPTMonetarioAtendimentos,
    faturamentoMonetarioTaxasServico: ptPTTaxasServico,
    faturamentoMonetarioComissaoVendedor: ptPTComissaoVendedor,
    faturamentoMonetarioPorPeriodo: ptPTPorPeriodo,
    faturamentoMonetarioPorModulo: ptPTPorModulo,
    faturamentoMonetarioFormasDePagamento: ptPTRelatorioFormasDePagamento,
    operacionalFornecedoresCredores: ptPTFornecedoresCredores,
    financasRepresentantes: ptPTRepresentantes,
    financasParametrosCategoriaSangria: ptPTCategoriaSangria,
    financasParametrosCentroDeCustos: ptPTCentroDeCustos,
    financasParametrosCadastroBandeiras: ptPTCadastroBandeiras,
    financasParametrosPlanoDeContas: ptPTPlanoDeContas,
    financasParametrosContasCorrente: ptPTContasCorrente,
    financasParametrosCFOP: ptPTCFOP,
    financasMovimentacoesContasReceber: ptPTContasReceber,
    financasComprasPedidosDeCompras: ptPTPedidosDeCompras,
    financasComprasRecebimentoMercadorias: ptPTRecebimentoMercadorias,
    financasVendasPedidosDeVendas: ptPTPedidosDeVendas,
    consultasRapidasCaixa: ptPTConsultasRapidasCaixa,
    tributacaoUfDestino: ptPTTributacaoUfDestino,
    modalSelecionarTurnos: ptPTModalSelecionarTurnos,
    modalSelecao: ptPTModalSelecao,
    parametrosModulosPub: ptPTParametrosModulosPub,
    relatorioSatisfacao: ptPTChatbotRelatorioSatisfacao,
    chatbotConfiguracao: ptPTChatbotConfiguracao,
    chatbotSuporte: ptPTChatbotSuporte,
    assistenteIA: ptPTAssistenteIA,
  },
};

const SUPPORTED_LANGS = ["pt-BR", "pt-PT"];
const DEFAULT_LANG = "pt-BR";

function detectLanguage() {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("language");

    if (lang && SUPPORTED_LANGS.includes(lang)) {
      return lang;
    }

    // CORREÇÃO: Utiliza o obterStorage para descriptografar os dados da sessão
    const sessao = obterStorage("_da") || obterStorage("sessao");
    const idioma = sessao?.idioma;

    if (idioma && SUPPORTED_LANGS.includes(idioma)) {
      return idioma;
    }

    return DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

const initialLanguage = detectLanguage();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: "pt-BR",
  supportedLngs: SUPPORTED_LANGS,
  ns: [
    "common",
    "consultasRapidasAtendimentos",
    "grupos",
    "minhaEmpresa",
    "dadosEmpresa",
    "canaisVenda",
    "funcionamentoEmpresa",
    "pagamento",
    "transacoes",
    "cadastroEmail",
    "cadastroProdutos",
    "minhasBalancas",
    "emissoresFiscais",
    "downloadAplicativos",
    "minhasImpressoras",
    "meusDispositivos",
    "formasDePagamento",
    "layoutsDeConferencia",
    "servicosDoSistema",
    "perfisUsuarios",
    "usuarios",
    "categoriasSangria",
    "confirmacaoImportacaoNfe",
    "contasCorrentes",
    "cadastraisClientes",
    "cadastraisFichaTecnica",
    "cadastraisProdutos",
    "cadastraisTributacaoICMS",
    "cadastraisTributacaoPiscofins",
    "cashback",
    "modalSelecionarClientes",
    "modalSelecionarLojas",
    "modalSelecionarInsumos",
    "selecionarGrupos",
    "selecionarFornecedoresPaginado",
    "selecionarFornecedores",
    "fiscaisListagemAuxEscrituracaoFiscal",
    "estoqueMovimentacoesInsumos",
    "relatoriosEstoqueGeral",
    "relatoriosEstoqueHistoricoMovimentacoes",
    "relatoriosEstoqueDisponivel",
    "relatoriosEstoqueEmFalta",
    "relatoriosFinancasContasAPagar",
    "relatoriosFinancasContasAReceber",
    "pedidosPedidosDeVenda",
    "faturamentoCurvaABC",
    "faturamentoCancelamentosItensCancelados",
    "faturamentoProdutosVendidosTop10",
    "faturamentoProdutosVendidosPorTabelaPreco",
    "faturamentoProdutosVendidosPorPacote",
    "faturamentoProdutosVendidosPorGrupo",
    "faturamentoProdutosVendidosItensVendidos",
    "faturamentoProdutosVendidosPorVendedor",
    "faturamentoModulosDelivery",
    "faturamentoModulosControleTempoProducao",
    "faturamentoMonetarioAnualMesMes",
    "faturamentoMonetarioAtendimentos",
    "faturamentoMonetarioTaxasServico",
    "faturamentoMonetarioComissaoVendedor",
    "faturamentoMonetarioPorPeriodo",
    "faturamentoMonetarioPorModulo",
    "faturamentoMonetarioFormasDePagamento",
    "modalSelecionarCaixas",
    "financasOperacionalConfiguracoes",
    "operacionalFornecedoresCredores",
    "financasRepresentantes",
    "financasParametrosCategoriaSangria",
    "financasParametrosCentroDeCustos",
    "financasParametrosCadastroBandeiras",
    "financasParametrosPlanoDeContas",
    "financasParametrosContasCorrente",
    "financasParametrosCFOP",
    "financasMovimentacoesContasReceber",
    "financasComprasPedidosDeCompras",
    "financasComprasRecebimentoMercadorias",
    "financasVendasPedidosDeVendas",
    "consultasRapidasCaixa",
    "tributacaoUfDestino",
    "modalSelecionarTurnos",
    "modalSelecao",
    "parametrosModulosPub",
    "relatorioSatisfacao",
    "chatbotConfiguracao",
    "chatbotSuporte",
    "assistenteIA",
  ],
  defaultNS: "common",
  interpolation: {
    escapeValue: false,
  },
  missingKeyHandler: (lngs, ns, key) => {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[i18n] Chave ausente: "${key}" no namespace "${ns}" para os idiomas: ${lngs.join(", ")}`,
      );
    }
  },
});

export default i18n;
